import React, { useState, useEffect } from 'react';
import { Dices, Presentation, Layout, Code2, Palette, BookText } from 'lucide-react';

import { BackgroundWaves } from './BackgroundVideo';
import { getRandomSuggestions, Suggestion } from "@/data/chatSuggestions";
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface WelcomeScreenProps {
  isDarkMode: boolean;
  selectedAgent: any;
  onSuggestionClick: (suggestion: string) => void;
  inputArea?: React.ReactNode;

  onOpenPricing?: () => void;
  onUpgrade?: () => void;
  user: any;
  isTempMode: boolean;
  planTier: string;
  isLoadingPlan?: boolean;
  isInputExpanded: boolean;
}

const getFirstNameFromUser = (user?: any, profileFullName?: string | null): string | null => {
  // Only use the name from user_profiles (profileFullName).
  // If it's not available, we don't fall back to auth metadata or email.
  if (!profileFullName) return null;

  const firstWord = profileFullName.trim().split(/\s+/)[0];
  if (!firstWord) return null;

  return firstWord;
};

const getTimeOfDayLabel = () => {
  const hour = new Date().getHours();

  if (hour >= 4 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  if (hour >= 21 && hour < 4) return 'night';
  return null;
};

const getGreetingText = (user?: any, profileFullName?: string | null): string => {
  const firstName = getFirstNameFromUser(user, profileFullName);
  const timeOfDay = getTimeOfDayLabel();

  if (!firstName) {
    const isDay = timeOfDay === 'morning' || timeOfDay === 'afternoon';
    return `How can I help you ${isDay ? 'today' : 'tonight'}?`;
  }

  const greetings = [
    // --- The "Front-Loaded" (Personal & Direct) ---
    `${firstName}, let’s do this.`,
    `${firstName}! Ready to roll?`,
    `${firstName}, the stage is yours.`,
    `${firstName}, hope you're having a good ${timeOfDay}.`,

    // --- The "Middle-Placement" (Conversational) ---
    `Hello, ${firstName}. What’s the plan?`,
    `Hey ${firstName}, let's get to work.`,
    `Welcome, ${firstName}. We're ready.`,
    `Hi ${firstName}, great to see you.`,

    // --- The "Action-Oriented" (Motivational) ---
    `Time to shine, ${firstName}.`,
    `Let's make it happen, ${firstName}.`,
    `Let’s crush it today, ${firstName}.`,
    `Full steam ahead, ${firstName}.`,

    // --- The "Service-Oriented" (Helpful) ---
    `How can I help you, ${firstName}?`,
    `Here to assist, ${firstName}.`,
    `Standing by for you, ${firstName}.`,
    `Your dashboard is ready, ${firstName}.`
  ];

  let hash = 0;
  for (let i = 0; i < firstName.length; i++) {
    hash = (hash * 31 + firstName.charCodeAt(i)) >>> 0;
  }

  const index = hash % greetings.length;
  return greetings[index];
};

export const WelcomeScreen = (props: WelcomeScreenProps) => {
  const { isDarkMode, onSuggestionClick, inputArea, onOpenPricing, onUpgrade, user, isTempMode = false, planTier, isLoadingPlan = false, isInputExpanded = false } = props;
  const [randomPrompts, setRandomPrompts] = useState<Suggestion[]>([]);
  const [greeting, setGreeting] = useState<string>('');
  const [profileFullName, setProfileFullName] = useState<string | null>(null);
  const [showGreeting, setShowGreeting] = useState(false);
  const [showPlanPill, setShowPlanPill] = useState(false);

  // Focus Input on Mount
  useEffect(() => {
    // Small timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusChatInput'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const prompts = getRandomSuggestions(4);
    setRandomPrompts(prompts);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfileFullName(null);
        return;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!error && data && data.full_name) {
        setProfileFullName(data.full_name as string);
      } else {
        setProfileFullName(null);
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    // If there is no authenticated user, do not use sessionStorage.
    // Just compute the greeting directly (will fall back when no profile name).
    let finalGreeting = "";

    if (!user) {
      finalGreeting = getGreetingText(undefined, profileFullName);
    } else {
      const sessionKey = `welcome_greeting_${user.id}`;
      const storedGreeting = sessionStorage.getItem(sessionKey);

      // When profileFullName is available, always recompute greeting with the
      // profile name and overwrite any stored value so it shows the correct name.
      if (profileFullName) {
        finalGreeting = getGreetingText(user, profileFullName);
        sessionStorage.setItem(sessionKey, finalGreeting);
      } else if (storedGreeting) {
        // If we don't yet have profileFullName, fall back to any stored greeting.
        finalGreeting = storedGreeting;
      } else {
        // As a final fallback for authenticated users without profileFullName,
        // compute and store a greeting (which will be the generic fallback text).
        finalGreeting = getGreetingText(user, profileFullName);
        sessionStorage.setItem(sessionKey, finalGreeting);
      }
    }

    setGreeting(finalGreeting);

    // Initial logic: if we have a name, show immediately. If not, delay.
    if (profileFullName) {
      setShowGreeting(true);
    } else {
      const timer = setTimeout(() => {
        setShowGreeting(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, profileFullName]);

  useEffect(() => {
    setShowPlanPill(false);

    if (planTier !== 'free') return;

    const timer = setTimeout(() => {
      setShowPlanPill(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [planTier, user]);

  const quickActions = [
    { label: 'Create slides', icon: Presentation, prompt: 'Create a slide deck on:' },
    { label: 'Build website', icon: Layout, prompt: 'Help me build a website for:' },
    { label: 'Story', icon: BookText, prompt: 'Help me create a story about:' },
    { label: 'Design', icon: Palette, prompt: 'Design a UI for:' },
    { label: 'Surprise', icon: Dices, prompt: randomPrompts[0]?.text || 'What can you do?' },
  ];

  const renderPromptCard = (suggestion: Suggestion, index: number) => {
    return (
      <button
        key={index}
        type="button"
        onClick={() => onSuggestionClick(suggestion.text)}
        className={cn(
          'w-full text-left px-2 py-1 text-sm',
          isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900',
          'hover:underline rounded'
        )}
      >
        {suggestion.text}
      </button>
    );
  };

  return (
    <div className={cn("flex-1 flex flex-col justify-center items-center p-[1px] sm:p-8 sm:pb-0 relative min-h-[calc(100vh-200px)] no-scrollbar",
      isInputExpanded ? "pb-0" : "pb-8"
    )}
    >
      <BackgroundWaves isDarkMode={isDarkMode} />

      <style>{`
        @property --border-angle {
          syntax: '<angle>';
          inherits: false;
          initial-value: 0deg;
        }
        
        @keyframes border-spin {
          0% { --border-angle: 0deg; }
          100% { --border-angle: 360deg; }
        }

        .animate-border {
          animation: border-spin 3s linear infinite;
        }
      `}</style>

      <div className="relative z-10 text-center max-w-4xl mx-auto w-full">
        {/* Welcome Message */}
        <div className={cn("relative flex flex-col select-none items-center justify-center sm:pt-6 pt-12",
          isInputExpanded ? "mb-0" : "mb-8 sm:mb-10"
        )}
        >

          {/* User Profile Icon */}

          {/* Only show plan pill for free plan users */}
          {planTier === 'free' && !isLoadingPlan && (
            <div
              className={cn(
                "items-center hidden justify-center mb-8 transition-all duration-500",
                showPlanPill ? "flex translate-y-0" : "hidden translate-y-2"
              )}
            >
              <div className={cn(
                "inline-flex items-center gap-1 rounded-full p-1 animate-border relative border border-transparent",
                isDarkMode
                  ? "[background:linear-gradient(45deg,#0f0f0f,#1a1a1a_50%,#0f0f0f)_padding-box,conic-gradient(from_var(--border-angle),rgba(255,255,255,0.1)_80%,_#3b82f6_86%,_#60a5fa_90%,_#3b82f6_94%,_rgba(255,255,255,0.1))_border-box]"
                  : "[background:linear-gradient(45deg,#f8fafc,#e2e8f0_50%,#f8fafc)_padding-box,conic-gradient(from_var(--border-angle),rgba(0,0,0,0.1)_80%,_#3b82f6_86%,_#60a5fa_90%,_#3b82f6_94%,_rgba(0,0,0,0.1))_border-box]"
              )}>
                <button
                  type="button"
                  onClick={() => onOpenPricing?.()}
                  className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium opacity-90 transition-colors",
                    isDarkMode ? "text-gray-100 hover:text-white" : "text-black/80 hover:text-black"
                  )}
                >
                  Free plan
                </button>
                <span
                  aria-hidden="true"
                  className={cn(
                    "h-4 w-px",
                    isDarkMode ? "bg-white/20" : "bg-black/15"
                  )}
                />
                <button
                  type="button"
                  onClick={() => onUpgrade?.()}
                  className={cn(
                    "px-3 py-1 rounded-full text-sm font-semibold bg-transparent transition-colors",
                    isDarkMode
                      ? "text-[#4EA3FF] hover:text-[#7bbcff]"
                      : "text-[#1a93fe] hover:text-[#007AFF]"
                  )}
                >
                  Upgrade
                </button>
              </div>
            </div>
          )}

          {/* Greeting Text */}
          <h1
            className={cn(
              "text-3xl sm:text-4xl instrument-serif-regular mb-6 transition-all duration-300 ease-in-out",
              isDarkMode ? "text-white" : "text-gray-900",
              showGreeting ? "opacity-100" : "opacity-0",
              isInputExpanded ? "mb-2" : "translate-y-0"
            )}
          >
            {greeting || getGreetingText(user, profileFullName)}
          </h1>

          <div className="w-full max-w-[820px] mx-auto px-[4px]">
            {inputArea}
          </div>

          <div className={cn(
            "mt-4 w-full max-w-[820px] mx-auto flex flex-wrap justify-center gap-2 px-2 transition-all duration-300 ease-in-out",
            isInputExpanded ? "opacity-0 h-0 mt-0 overflow-hidden" : "opacity-100"
          )}>
            {quickActions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => onSuggestionClick(a.prompt)}
                  className={cn(
                    "inline-flex items-center bg-transparent gap-2 rounded-full px-3 py-2 text-xs sm:text-sm border transition-colors",
                    isDarkMode
                      ? "border-[#484839] text-gray-200 hover:bg-[#383839]"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  )}
                >
                  <Icon className={cn("w-3.5 h-3.5", isDarkMode ? "text-gray-400" : "text-gray-500")} />
                  <span>{a.label}</span>
                </button>
              );
            })}
          </div>

          {/* Badge for non-authenticated users */}
          {!user && (
            <div className="relative hidden">
              <button
                className={`rounded-full h-[35px] w-[200px] mx-auto animate-border relative
                  ${isDarkMode
                    ? '[background:linear-gradient(45deg,#0f0f0f,#1a1a1a_50%,#0f0f0f)_padding-box,conic-gradient(from_var(--border-angle),rgba(255,255,255,0.1)_80%,_#3b82f6_86%,_#60a5fa_90%,_#3b82f6_94%,_rgba(255,255,255,0.1))_border-box]'
                    : '[background:linear-gradient(45deg,#f8fafc,#e2e8f0_50%,#f8fafc)_padding-box,conic-gradient(from_var(--border-angle),rgba(0,0,0,0.1)_80%,_#3b82f6_86%,_#60a5fa_90%,_#3b82f6_94%,_rgba(0,0,0,0.1))_border-box]'
                  } border border-transparent text-sm font-medium transition-colors duration-200 select-none relative z-10
                  ${isDarkMode ? 'text-white bg-transparent' : 'text-black bg-transparent'}`}
              >
                The Superman of LLMs
              </button>
            </div>
          )}
        </div>

        {/* Suggestions - minimal list */}
        {/* <div className="mb-6 relative z-10 w-full max-w-md mx-auto px-4">
          <div className="flex flex-col space-y-2">
            {randomPrompts.map((suggestion, index) => renderPromptCard(suggestion, index))}
          </div>
        </div> */}

        {/* Privacy and Terms Links */}
        {/* <div className="flex justify-center items-center gap-6 mt-8 mb-16 relative z-10">
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 sm:px-4 px-2 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'text-white/80 bg-black/20 hover:bg-black/30' 
                : 'text-black/80 bg-gray-200/60 hover:bg-gray-300/60'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium"><span className="hidden sm:inline">Privacy Policy</span><span className="sm:hidden">Privacy</span></span>
          </a>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'text-white/80 bg-black/20 hover:bg-black/30' 
                : 'text-black/80 bg-gray-200/60 hover:bg-gray-300/60'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm font-medium"><span className="hidden sm:inline">Terms of Service</span><span className="sm:hidden">T&C</span></span>
          </a>
        </div> */}
      </div>
    </div>
  );
};